import { Box, Stack } from '@mui/material'

import { MetaText } from '@/components/atoms/MetaText'
import { useTracingReportEditing } from '@/apps/latest/features/tracing-report/useTracingReportEditing'
import {
  describeItemOriginLines,
  type TracingReportItem,
} from '@/features/internal/tracing-report'
import { type YakurekiDetail } from '@/shared/types/yakurekiCore'

import { SectionRenderer } from './SectionRenderer'

interface Props {
  event: YakurekiDetail
  items: TracingReportItem[]
}

// 採用行のみの連結テキスト (カードヘッダー右上の全体コピーが使う)
export const buildTracingReportCopyText = (
  items: TracingReportItem[]
): string =>
  items
    .flatMap((item) => item.report_json ?? [])
    .filter((line) => line.is_selected ?? true)
    .map((line) => line.content)
    .join('\n')

// トレーシングレポートタブの中身 (HomeVisitReportPanel と同型)。
// 本文は SOAP と同じ操作感 (行頭チェックボックスで採否 / 本文クリックで編集) を
// SectionRenderer で共用。単一文書タブのため見出し行は持たず (label 省略)、
// コピーはカードヘッダー右上 (PostRecordingView 側) が担う。
// 由来 meta (検知ルール・宛先) は本文下の脚注。
export const TracingReportPanel = ({ event, items }: Props) => {
  const editing = useTracingReportEditing({ yakurekiId: event.id, items })

  return (
    <Box>
      <Stack spacing={2}>
        {items.map((item) => (
          <Box key={item.id}>
            {item.report_json === null ? (
              <MetaText>レポートを生成しています…</MetaText>
            ) : (
              <SectionRenderer
                sectionKey={item.id}
                items={item.report_json}
                editing={editing}
                strike={{
                  isStruck: (line) => !(line.is_selected ?? true),
                  toggleLine: (_line, i) => editing.toggleSelect(item.id, i),
                }}
                medications={[]}
                onPlaceholderReplace={async () => false}
              />
            )}
            {/* 由来 meta は本文の下に脚注として添える (主役はレポート本文) */}
            <Box sx={{ mt: 1 }}>
              {describeItemOriginLines(item).map((line) => (
                <MetaText key={line} sx={{ display: 'block' }}>
                  {line}
                </MetaText>
              ))}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}
